import { create } from 'zustand';
import { CoffeeEntity } from '../types/CoffeeEntity';
import type { AlgoliaSearchParams } from '../services/algoliaSearch';

// Simple debounce implementation to avoid external dependency
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): T & { cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debounced = function (this: any, ...args: Parameters<T>) {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func.apply(this, args);
      timeoutId = null;
    }, wait);
  } as T & { cancel: () => void };

  debounced.cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced;
}

export type EnrichedShop = CoffeeEntity & {
  lat?: number;
  lng?: number;
  imageUrl?: string | null;
  image?: string | null;
  logoUrl?: string | null;
  phone?: string | null;
  website?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  distanceMiles?: number;
  [key: string]: any;
};

export type SearchFilters = {
  roasterOnly: boolean;
  sellsBeans: boolean;
  roastsOrSellsBeans: boolean;
  dogFriendly: boolean;
  independent: boolean;
  farmersMarket: boolean;
  organic: boolean;
  givesBack: boolean;
};

export const DEFAULT_FILTERS: SearchFilters = {
  roasterOnly: false,
  sellsBeans: false,
  roastsOrSellsBeans: false,
  dogFriendly: false,
  independent: false,  // Show both independent and chain shops
  farmersMarket: false,
  organic: false,
  givesBack: false,
};

export type SearchResults = {
  shops: EnrichedShop[];
  roasters: EnrichedShop[];
  totalShops?: number;
  totalRoasters?: number;
  hasMoreShops?: boolean;
  hasMoreRoasters?: boolean;
};

export type ViewMode = 'hero' | 'list' | 'map';

export type PendingMapFocus = {
  id: string;
  latitude?: number;
  longitude?: number;
  [key: string]: any;
} | null;

type SearchState = {
  query: string;
  results: SearchResults;
  filters: SearchFilters;
  currentRequestId?: string;
  viewMode: ViewMode;
  hasUserChosenView: boolean;
  pendingMapFocus: PendingMapFocus;
  hasSearchedOnce: boolean;
  loading: boolean;
  loadingMore: boolean;
  errorMsg?: string;
  userLocation: [number, number] | null;
  currentPage: number;
};

type SearchActions = {
  setViewMode: (mode: ViewMode) => void;
  toggleViewMode: () => void;
  setQuery: (q: string) => void;
  setFilters: (partial: Partial<SearchFilters>) => void;
  setPendingMapFocus: (focus: PendingMapFocus) => void;
  setUserLocation: (coords: [number, number] | null) => void;
  runSearch: () => Promise<void> | void;
  runSearchDebounced: (() => void) & { cancel: () => void };
  runSearchWithQuery?: (q: string) => Promise<void> | void;
  loadMoreResults: () => Promise<void>;
  resetFilters: () => void;
  resetSearch: () => void;
  clearSearchResults: () => void;
};

export type SearchStore = SearchState & SearchActions;

// Utility: shallow compare plain objects
const shallowEqual = (a: Record<string, any>, b: Record<string, any>) => {
  if (a === b) return true;
  const ak = Object.keys(a),
    bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (let i = 0; i < ak.length; i++) {
    const k = ak[i];
    if (a[k] !== b[k]) return false;
  }
  return true;
};

// Normalize booleans coming from UI (undefined/null/"true" → boolean)
const normalizeFilters = (
  prev: Record<string, any>,
  partial: Record<string, any>,
) => {
  const next: Record<string, any> = { ...prev };
  for (const [k, v] of Object.entries(partial ?? {})) {
    next[k] =
      typeof v === 'boolean'
        ? v
        : v === 'true'
        ? true
        : v === 'false'
        ? false
        : v == null
        ? v
        : !!v;
  }
  return next;
};

function mapPublicSearchError(e: unknown): string {
  const code = (e as any)?.code ?? '';
  return code === 'permission-denied'
    ? 'Unable to load shops due to a permission issue. Please retry.'
    : 'Failed to load shops. Please retry.';
}


export const useSearchStore = create<SearchState & SearchActions>((set, get) => {
  // Create debounced search function outside the store object
  const debouncedSearchImpl = debounce(() => {
    const store = get();
    if (store.runSearch) {
      store.runSearch();
    }
  }, 500);

  return {
    query: '',
    results: { shops: [], roasters: [] },
    viewMode: 'hero',
    hasUserChosenView: false,
    // ensure filters always exist
    filters: DEFAULT_FILTERS,
    currentRequestId: undefined,
    pendingMapFocus: null,
    hasSearchedOnce: false,
    currentPage: 0,
    loadingMore: false,
    loading: false,
    errorMsg: undefined,
    userLocation: null,
    runSearchDebounced: debouncedSearchImpl,
    setQuery: (q: string) => {
      set((s) => ({
        query: q,
        ...(s.hasUserChosenView ? {} : { viewMode: 'list' }),
      }));
    },
    setFilters: (partial) => {
      const prev = get().filters ?? {};
      const next = normalizeFilters(prev, partial as any) as SearchFilters;

      // No-op if unchanged
      if (shallowEqual(prev, next)) {
        return;
      }

      // Apply new filters
      set({ filters: next });

      // 🔎 Use debounced search to avoid spamming worker on rapid filter changes.
      // A filter with no query is still a valid browse ("all roasters near me"),
      // so re-run whenever there's a query OR any active filter — EXCEPT on the
      // map view, where a query-less global search would dump nationwide results
      // onto the map and yank the viewport; there the map re-runs its own
      // bounded area search when filters change.
      const q = (get().query || '').trim();
      const anyFilterActive = Object.values(next).some(Boolean);
      const onMap = get().viewMode === 'map';
      if (q || (anyFilterActive && !onMap)) {
        get().runSearchDebounced();
      } else if (!q && !anyFilterActive && get().hasSearchedOnce) {
        // Last filter switched off with no query — restore the browse state
        // instead of leaving a stale "no results" search.
        get().clearSearchResults();
      }
    },
  setViewMode: (view) => {
    set({ viewMode: view, hasUserChosenView: true });
    // Filters toggled on the map don't run the global search (the map does its
    // own bounded area search). If the user switches to the list with filters
    // active and nothing loaded, backfill the list now.
    if (view === 'list') {
      const { query: q, filters: f, results } = get();
      const anyFilterActive = Object.values(f ?? {}).some(Boolean);
      if (!q.trim() && anyFilterActive && results.shops.length === 0) {
        get().runSearchDebounced();
      }
    }
  },
  toggleViewMode: () => {
    const current = get().viewMode;
    set({
      viewMode: current === 'map' ? 'list' : 'map',
      hasUserChosenView: true,
    });
  },
  setPendingMapFocus: (focus) => {
    set({ pendingMapFocus: focus });
  },
  setUserLocation: (coords) => {
    set({ userLocation: coords });
  },
  runSearch: async () => {
    set({
      loading: true,
      errorMsg: undefined,
      hasSearchedOnce: true,
      results: { shops: [], roasters: [] },
    });

    const requestId = `${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;
    set({ currentRequestId: requestId });

    try {
      const q = (get().query || '').trim();
      const f = get().filters ?? {};

      // Request geolocation on first search (deferred from page load for Lighthouse perf)
      let loc = get().userLocation;
      if (!loc && typeof navigator !== 'undefined' && navigator.geolocation) {
        // Kick off geolocation request and wait up to 1.5s for it
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const coords: [number, number] = [pos.coords.latitude, pos.coords.longitude];
            set({ userLocation: coords });
          },
          () => { /* silently ignore errors */ },
          { enableHighAccuracy: false, maximumAge: 30_000, timeout: 10_000 },
        );
        await new Promise<void>((resolve) => {
          let elapsed = 0;
          const interval = setInterval(() => {
            elapsed += 50;
            if (get().userLocation || elapsed >= 1500) {
              clearInterval(interval);
              resolve();
            }
          }, 50);
        });
        loc = get().userLocation;
      }

      const { searchAlgolia } = await import('../services/algoliaSearch');
      const { shops, roasters } = await searchAlgolia({
        query: q,
        filters: {
          independent: !!f.independent,
          dogFriendly: !!f.dogFriendly,
          farmersMarket: !!f.farmersMarket,
          roastsOrSellsBeans: !!(f.roastsOrSellsBeans || f.sellsBeans || f.roasterOnly),
          organic: !!f.organic,
          givesBack: !!f.givesBack,
          roasterOnly: !!f.roasterOnly,
          sellsBeans: !!(f.sellsBeans || f.roastsOrSellsBeans),
        },
        aroundLatLng: loc ? `${loc[0]},${loc[1]}` : undefined,
        hitsPerPage: 1000,
      });

      // Ignore stale responses
      if (get().currentRequestId !== requestId) return;

      set((s) => ({
        results: { 
          shops, 
          roasters,
          totalShops: shops.length,
          totalRoasters: roasters.length,
          hasMoreShops: shops.length >= 1000,
          hasMoreRoasters: roasters.length >= 1000,
        },
        loading: false,
        currentPage: 0,
        errorMsg: undefined,
        ...(s.hasUserChosenView ? {} : { viewMode: 'list' }),
      }));
    } catch (e) {
      if (get().currentRequestId !== requestId) return;
      set({
        results: { shops: [], roasters: [] },
        loading: false,
        errorMsg: mapPublicSearchError(e),
      });
    }
  },
  runSearchWithQuery: async (q: string) => {
    set({ query: q });
    await get().runSearch(); // uses the freshly set query
  },
  loadMoreResults: async () => {
    const { query: q, filters: f, userLocation: loc, currentPage, loadingMore, results } = get();
    
    if (loadingMore || !q.trim()) return;
    
    set({ loadingMore: true });
    
    try {
      const { searchAlgolia } = await import('../services/algoliaSearch');
      const nextPage = currentPage + 1;
      const { shops, roasters } = await searchAlgolia({
        query: q,
        filters: {
          independent: !!f.independent,
          dogFriendly: !!f.dogFriendly,
          farmersMarket: !!f.farmersMarket,
          roastsOrSellsBeans: !!(f.roastsOrSellsBeans || f.sellsBeans || f.roasterOnly),
          organic: !!f.organic,
          givesBack: !!f.givesBack,
          roasterOnly: !!f.roasterOnly,
          sellsBeans: !!(f.sellsBeans || f.roastsOrSellsBeans),
        },
        aroundLatLng: loc ? `${loc[0]},${loc[1]}` : undefined,
        hitsPerPage: 1000,
        page: nextPage,
      });
      
      set((s) => ({
        results: {
          shops: [...s.results.shops, ...shops],
          roasters: [...s.results.roasters, ...roasters],
          totalShops: s.results.shops.length + shops.length,
          totalRoasters: s.results.roasters.length + roasters.length,
          hasMoreShops: shops.length >= 1000,
          hasMoreRoasters: roasters.length >= 1000,
        },
        currentPage: nextPage,
        loadingMore: false,
      }));
    } catch (e) {
      set({ loadingMore: false });
    }
  },
  // reset to defaults (used by resetSearch)
  resetFilters: () => {
    set({ filters: DEFAULT_FILTERS });
  },
  clearSearchResults: () => {
    debouncedSearchImpl.cancel();
    set({
      query: '',
      results: { shops: [], roasters: [] },
      filters: DEFAULT_FILTERS,
      hasSearchedOnce: false,
      loading: false,
      errorMsg: undefined,
      pendingMapFocus: null,
      // viewMode intentionally NOT reset — stay on current view
    });
  },
  resetSearch: () => {
    // Cancel any pending debounced searches
    debouncedSearchImpl.cancel();
    set({
      query: '',
      results: { shops: [], roasters: [] },
      filters: DEFAULT_FILTERS,
      viewMode: 'hero',
      hasUserChosenView: false,
      currentRequestId: undefined,
      pendingMapFocus: null,
      hasSearchedOnce: false,
      loading: false,
      errorMsg: undefined,
      // preserve userLocation across resets
    });
  },
};
});

export const selectView = (s: SearchStore) => ({
  viewMode: s.viewMode,
  setViewMode: s.setViewMode,
  toggleViewMode: s.toggleViewMode,
});
